export type RemoveElementTarget = {
  key?: string
  selector?: string
  id?: string
  sectionKey?: string
}

export type RemoveElementFailure = 'not-found' | 'guarded' | 'ambiguous'

export type RemoveElementResult =
  | { ok: true; html: string }
  | { ok: false; reason: RemoveElementFailure }

const GUARDED_SELECTOR = 'section.ara-section, .ara-section-body'
const ARTIFACT_SELECTOR = '.ara-target-actions, .sec-edit-btn'
const STANDALONE_CELL_SELECTOR = '[data-edit-cell], .stat-cell'
const SEPARATOR_SELECTOR = '.stat-vrule, .card-rule'

function cssEscape(value: string): string {
  const scope = globalThis as { CSS?: { escape?: (value: string) => string } }
  return scope.CSS?.escape ? scope.CSS.escape(value) : value
}

function isEffectiveChild(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').trim() !== ''
  if (node.nodeType === Node.COMMENT_NODE) return false
  if (node.nodeType !== Node.ELEMENT_NODE) return false
  return !(node as Element).matches(ARTIFACT_SELECTOR)
}

function isDecorative(el: Element): boolean {
  if (el.tagName.toLowerCase() === 'svg') return false
  if ((el.textContent ?? '').trim() !== '') return false
  return !el.matches('[data-edit-key], canvas, img, video, .mermaid') &&
    !el.querySelector('[data-edit-key], canvas, img, video, .mermaid')
}

function hasMeaningfulChild(parent: Element): boolean {
  const insideGroup = Boolean(parent.closest('[data-edit-group]'))
  for (const node of Array.from(parent.childNodes)) {
    if (!isEffectiveChild(node)) continue
    if (node.nodeType === Node.TEXT_NODE) return true
    if (!insideGroup && isDecorative(node as Element)) continue
    return true
  }
  return false
}

function countSelectorMatches(root: ParentNode, selector: string): number | null {
  try {
    return root.querySelectorAll(selector).length
  } catch {
    return null
  }
}

type ResolvedTarget = { el: Element } | 'ambiguous'

function resolveTarget(root: ParentNode, target: RemoveElementTarget): ResolvedTarget | null {
  if (target.key) {
    const el = root.querySelector(`[data-edit-key="${cssEscape(target.key)}"]`)
    if (el) return { el }
  }
  if (target.selector) {
    const count = countSelectorMatches(root, target.selector)
    if (count !== null) {
      // A selector matching several elements (e.g. a generic data-edit-group
      // repeated across cards) has no "first match" the picker could have
      // meant; only an explicit id can disambiguate it. This also holds when
      // a stale key was sent alongside the selector and no longer resolves.
      if (count > 1) {
        const byId = target.id ? root.querySelector(`#${cssEscape(target.id)}`) : null
        return byId ? { el: byId } : 'ambiguous'
      }
      const el = root.querySelector(target.selector)
      if (el) return { el }
    }
  }
  if (target.id) {
    const el = root.querySelector(`#${cssEscape(target.id)}`)
    if (el) return { el }
  }
  return null
}

function promoteTableCellToRow(el: Element): Element {
  if (!el.matches('td, th')) return el
  const row = el.closest('tr')
  if (!row || !row.closest('tbody')) return el
  return row
}

function promoteToCell(el: Element): Element {
  const cell = el.closest(STANDALONE_CELL_SELECTOR)
  if (!cell || cell === el) return el
  if (cell.closest(GUARDED_SELECTOR) === cell) return el
  return cell
}

function dropAdjacentSeparator(el: Element): void {
  const prev = el.previousElementSibling
  if (prev?.matches(SEPARATOR_SELECTOR)) {
    prev.remove()
    return
  }
  const next = el.nextElementSibling
  if (next?.matches(SEPARATOR_SELECTOR)) next.remove()
}

export function removeElement(sectionHtml: string, target: RemoveElementTarget): RemoveElementResult {
  const container = document.createElement('div')
  container.innerHTML = sectionHtml

  const resolved = resolveTarget(container, target)
  if (resolved === 'ambiguous') return { ok: false, reason: 'ambiguous' }
  if (!resolved) return { ok: false, reason: 'not-found' }
  const el = promoteTableCellToRow(promoteToCell(resolved.el))
  if (el.closest(GUARDED_SELECTOR) === el) return { ok: false, reason: 'guarded' }

  const scope = el.closest('section.ara-section') ?? container
  const chain: Element[] = []
  let cursor = el.parentElement
  while (cursor && cursor !== scope) {
    chain.push(cursor)
    cursor = cursor.parentElement
  }

  dropAdjacentSeparator(el)
  el.remove()

  for (const ancestor of chain) {
    if (ancestor.matches(GUARDED_SELECTOR)) break
    if (hasMeaningfulChild(ancestor)) break
    dropAdjacentSeparator(ancestor)
    ancestor.remove()
  }

  return { ok: true, html: container.innerHTML }
}
